import type { ComponentProps } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost';

/** The one button treatment for the portal (links styled as buttons use `buttonClass`). */
const BASE =
  'inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed';
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-accent text-on-accent hover:bg-accent-hover disabled:bg-disabled disabled:text-muted',
  secondary:
    'border border-line-strong bg-surface text-fg hover:border-muted hover:bg-raised disabled:text-faint',
  ghost: 'text-fg-2 hover:bg-raised hover:text-fg disabled:text-faint',
};

export function buttonClass(variant: Variant = 'primary', extra = ''): string {
  return `${BASE} ${VARIANTS[variant]} ${extra}`.trim();
}

/** Includes `ref` (React 19 passes it as a normal prop). */
export interface ButtonProps extends ComponentProps<'button'> {
  variant?: Variant;
}

/** The portal's base button. `type` defaults to "button" so it never submits a form by accident. */
export function Button({
  variant = 'primary',
  className = '',
  type = 'button',
  ...props
}: ButtonProps) {
  return <button type={type} className={buttonClass(variant, className)} {...props} />;
}
